// Relic Shards (CHANGELOG 6.6): a slow but sure road to one Legendary of the player's
// choice. Shards come from dismantling Epic gear, the weekly contract, the Sunday boss
// and risky event choices that pay off. 60 of them forge the Legendary of an item the
// player has found at least in Epic (checked in the collection book). The forged item
// is account bound: it never changes hands.

import { foundAtLeastEpic, type CollectionState } from '../collection';
import { ITEM_BY_ID, isSlotLocked, itemDisplayName, itemTemplate } from '../content/items';
import { RECIPE_BY_ID, type RecipeDef } from '../content/recipes';
import { Rng } from '../rng';
import type { Rarity } from '../types';
import { MVP_ITEMS } from './mvpItems';

export const RELIC_RULES = {
  /** Shards for one forged Legendary. */
  forgeCost: 60,
  forgeWorkshop: 3,
  forgeMinutes: 180,
  forgeXp: 300,
  /** Dismantling an Epic item gives 1 or 2 (fixed per item, so the preview is exact). */
  dismantleEpic: [1, 2] as const,
  /** Sunday boss: everyone who fought, plus the top 10% and a win. */
  /** Krug 5 (F5): 6 (was 4). */
  bossJoin: 6,
  bossTop: 2,
  bossVictory: 1,
  /** How often a risky event choice that succeeds also turns up a shard. */
  eventChance: 0.2,
} as const;

/**
 * 6. 10. 2026 (owner): a weapon from the Relic Forge draws the attributes its class uses with a bigger weight inside the
 * attribute row (ranged: PER and AGI, melee: STR; the others 1), since the player pays 60 shards there. Drops stay fully
 * random (the hunt for the perfect item). `on: false` is the forge before (every attribute 1).
 */
export const RELIC_ATTRS = { on: true, weight: 3, ranged: ['per', 'agi'], melee: ['str'] } as { on: boolean; weight: number; ranged: string[]; melee: string[] };

/** The attribute weights of a weapon's class in the Relic Forge (undefined: not a weapon, or the rule off). */
export function relicAttrWeights(templateId: string): Record<string, number> | undefined {
  const c = itemTemplate(templateId).weaponClass;
  if (!RELIC_ATTRS.on || !c) return undefined;
  const use = c.startsWith('ranged') ? RELIC_ATTRS.ranged : RELIC_ATTRS.melee;
  return Object.fromEntries(use.map((k) => [k, RELIC_ATTRS.weight]));
}

export const RELIC_RECIPE_PREFIX = 'relic:';

const MVP_IDS = new Set<string>(MVP_ITEMS.map((i) => i.id));

export function relicRecipeId(templateId: string): string {
  return `${RELIC_RECIPE_PREFIX}${templateId}`;
}

export function isRelicRecipe(recipeId: string): boolean {
  return recipeId.startsWith(RELIC_RECIPE_PREFIX);
}

/** Forge recipe for one item, or null when it cannot be forged (not an MVP item, or a locked slot). */
export function relicRecipe(templateId: string): RecipeDef | null {
  const t = ITEM_BY_ID[templateId];
  if (!t || !MVP_IDS.has(templateId) || isSlotLocked(t.slot)) return null;
  return {
    id: relicRecipeId(templateId),
    name: itemDisplayName(t, 'legendary'),
    group: 'relic',
    output: { kind: 'item', templateId, rarity: 'legendary' },
    workshop: RELIC_RULES.forgeWorkshop,
    cost: { relic_shard: RELIC_RULES.forgeCost },
    minutes: RELIC_RULES.forgeMinutes,
    xp: RELIC_RULES.forgeXp,
    flavor: 'Forged from Relic Shards. Bound to you: it can never be traded or stolen.',
  };
}

/** Any recipe by id, including the forge recipes that are not on the fixed list. */
export function recipeById(recipeId: string): RecipeDef | undefined {
  return RECIPE_BY_ID[recipeId] ?? (isRelicRecipe(recipeId) ? (relicRecipe(recipeId.slice(RELIC_RECIPE_PREFIX.length)) ?? undefined) : undefined);
}

/** Items the player may forge: found at least in Epic. */
export function relicOptions(collection: CollectionState | null | undefined): string[] {
  return MVP_ITEMS.map((i) => i.id).filter((id) => relicRecipe(id) !== null && foundAtLeastEpic(collection, id));
}

/** Shards from dismantling this item (Epic only). */
export function relicShardsFromDismantle(item: { id: string; rarity: Rarity }): number {
  if (item.rarity !== 'epic') return 0;
  const [lo, hi] = RELIC_RULES.dismantleEpic;
  return new Rng(`${item.id}:relic`).int(lo, hi);
}
